import { formatDateForApi } from '@/utils/datePresentation';
import { getOptionCode, toPositiveOptionId } from '@/utils/optionValues';
import { toBooleanFlag } from '@/utils/dataNormalization';
import { toAmount } from '@/utils/numberValue';
import { SALES_TEXT } from '@/texts/catalog/sales';

const buildTodayDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const addOneMonthPreservingDay = (date) => {
  const source = date instanceof Date ? date : buildTodayDate();
  const year = source.getFullYear();
  const month = source.getMonth();
  const day = source.getDate();

  const nextMonthStart = new Date(year, month + 1, 1);
  const nextMonthLastDay = new Date(year, month + 2, 0).getDate();
  nextMonthStart.setDate(Math.min(day, nextMonthLastDay));

  return nextMonthStart;
};

const SALE_MODAL_SECTION_TEXT = SALES_TEXT.modal.sections;

export const SALE_MODAL_SECTIONS = [
  {
    key: 'general',
    title: SALE_MODAL_SECTION_TEXT.general.title,
    description: SALE_MODAL_SECTION_TEXT.general.description,
    icon: 'fa-file-text-o',
  },
  {
    key: 'configuration',
    title: SALE_MODAL_SECTION_TEXT.configuration.title,
    description: SALE_MODAL_SECTION_TEXT.configuration.description,
    icon: 'fa-cogs',
  },
  {
    key: 'details',
    title: SALE_MODAL_SECTION_TEXT.details.title,
    description: SALE_MODAL_SECTION_TEXT.details.description,
    icon: 'fa-align-left',
  },
  {
    key: 'quote',
    title: SALE_MODAL_SECTION_TEXT.quote.title,
    description: SALE_MODAL_SECTION_TEXT.quote.description,
    icon: 'fa-file-text-o',
  },
];

export const createInitialSaleForm = () => ({
  saleDate: buildTodayDate(),
  deliveryDate: addOneMonthPreservingDay(buildTodayDate()),
  startDate: buildTodayDate(),
  paymentDate: buildTodayDate(),
  cfdiDate: buildTodayDate(),
  code: '',
  customer: null,
  saleName: '',
  service: null,
  area: null,
  tags: [],
  status: null,
  subtotal: 0,
  budgetGeneral: 0,
  agencyAmount: 0,
  mediaAmount: 0,
  currencyCode: 'MXN',
  exchangeRate: 1,
  voucherType: 'cfdi',
  frequency: 'monthly',
  paymentsCount: 1,
  firstInstallmentOption: null,
  periodicityOption: null,
  withoutTaxes: false,
  taxType: 'individual',
  taxes: [],
  taxGroup: null,
  manager: null,
  seller: null,
  quoteMode: 'url',
  quoteUrl: '',
  quoteFile: null,
  quoteType: null,
  quotePrincipal: null,
  quoteExtras: [],
  scope: '',
  comments: '',
});

export const createInitialSaleOptions = () => ({
  services: [],
  statuses: [],
  areas: [],
  sellers: [],
  managers: [],
  quoteTypes: [],
  quoteCatalogs: [],
  documentTypes: [],
  taxModes: [],
  taxes: [],
  taxGroups: [],
  periodicities: [],
});

export const getDefaultStatusOption = (statusOptions) => {
  const options = (Array.isArray(statusOptions) ? statusOptions : [])
    .filter((option) => String(option?.value ?? '').trim() !== '');

  if (options.length === 0) {
    return null;
  }

  const preferredCodes = Array.isArray(SALES_TEXT.modal.statusDefaults.preferredCodes)
    ? SALES_TEXT.modal.statusDefaults.preferredCodes
    : [];

  const preferredByCode = options.find((option) => {
    const normalizedCode = String(getOptionCode(option, { fallback: '' })).trim().toLowerCase();
    return preferredCodes.includes(normalizedCode);
  });

  if (preferredByCode) {
    return preferredByCode;
  }

  return options.find((option) => toBooleanFlag(option?.meta?.is_open ?? option?.is_open)) || options[0] || null;
};

export const applyDefaultStatus = (form, statusOptions) => {
  if (form?.status) {
    return;
  }

  const defaultStatus = getDefaultStatusOption(statusOptions);
  if (defaultStatus) {
    form.status = defaultStatus;
  }
};

export const syncAgencyAmount = (form, isAdvertisingEnabled) => {
  const subtotal = toAmount(form?.subtotal);

  if (!isAdvertisingEnabled) {
    form.agencyAmount = subtotal;
    return;
  }

  const mediaAmount = toAmount(form?.mediaAmount);
  const agencyAmount = subtotal - mediaAmount;

  form.agencyAmount = Number.isFinite(agencyAmount) ? agencyAmount : 0;
};

export const buildSalePayload = (form, {
  isAdvertisingEnabled = true,
  saleKind = 'business',
  taxPreview = null,
} = {}) => {
  const subtotal = toAmount(form?.subtotal);
  const budgetGeneral = toAmount(form?.budgetGeneral);
  const agencyAmount = isAdvertisingEnabled ? toAmount(form?.agencyAmount) : subtotal;
  const mediaAmount = isAdvertisingEnabled ? toAmount(form?.mediaAmount) : 0;
  const isFlatRate = String(saleKind || '').trim().toLowerCase() === 'flat-rate';

  const paymentsCount = Math.max(1, Number(form?.paymentsCount || 1) || 1);
  const flatRateTaxPreview = taxPreview && typeof taxPreview === 'object'
    ? taxPreview
    : null;

  const quotePrincipalValue = String(form?.quotePrincipal?.value || '').trim();
  const quoteExtras = (Array.isArray(form?.quoteExtras) ? form.quoteExtras : [])
    .map((item) => String(item?.value || '').trim())
    .filter(Boolean);

  const payload = {
    company_id: toPositiveOptionId(form?.customer),
    customer_id: toPositiveOptionId(form?.customer),
    code: String(form?.code || '').trim(),
    name: String(form?.saleName || '').trim(),
    product_id: toPositiveOptionId(form?.service),
    sale_date: formatDateForApi(form?.saleDate, { emptyValue: null, allowString: true }),
    delivery_date: formatDateForApi(form?.deliveryDate, { emptyValue: null, allowString: true }),
    area_id: toPositiveOptionId(form?.area),
    tags: (Array.isArray(form?.tags) ? form.tags : []).map((tag) => String(tag || '').trim()).filter(Boolean),
    status_sale_id: toPositiveOptionId(form?.status),
    subtotal,
    sale_price: subtotal,
    budget_general: budgetGeneral,
    sale_import: agencyAmount,
    agency_amount: agencyAmount,
    media_amount: mediaAmount,
    advertising_amount: mediaAmount,
    manager_id: toPositiveOptionId(form?.manager),
    seller_id: toPositiveOptionId(form?.seller),
    quote_type_id: toPositiveOptionId(form?.quoteType),
    quote_id: toPositiveOptionId(form?.quotePrincipal),
    quote_type: quotePrincipalValue,
    quote_mode: form?.quoteMode,
    quote_document: form?.quoteMode === 'url' ? String(form?.quoteUrl || '').trim() : '',
    quote_url: form?.quoteMode === 'url' ? String(form?.quoteUrl || '').trim() : '',
    quote_file: form?.quoteMode === 'file' ? form?.quoteFile : null,
    quote_is_file: form?.quoteMode === 'file' ? 1 : 0,
    quote_principal: quotePrincipalValue,
    quote_extras: quoteExtras,
    scope: String(form?.scope || '').trim(),
    comments: String(form?.comments || '').trim(),
  };

  if (isFlatRate) {
    const frequency = String(form?.frequency || 'unique').trim().toLowerCase() || 'unique';
    const voucherType = String(form?.voucherType || 'cfdi').trim().toLowerCase() || 'cfdi';

    payload.voucher_type = voucherType;
    payload.special = voucherType === 'simple_note' ? 1 : 0;
    payload.amount = subtotal;
    payload.frequency = frequency;
    payload.payments_count = paymentsCount;
    payload.expenses_count = paymentsCount;
    payload.count_payment = paymentsCount;
    payload.date = formatDateForApi(form?.startDate, { emptyValue: null, allowString: true });
    payload.payment_date = formatDateForApi(form?.paymentDate, { emptyValue: null, allowString: true });
    payload.cfdi_date = voucherType === 'simple_note'
      ? null
      : formatDateForApi(form?.cfdiDate, { emptyValue: null, allowString: true });
    payload.first_installment = String(form?.firstInstallmentOption?.value || '').trim();
    payload.periodicity_code = String(form?.periodicityOption?.value || '').trim().toLowerCase();
    payload.tax_type = Boolean(form?.withoutTaxes)
      ? 'individual'
      : (String(form?.taxType || 'individual').trim().toLowerCase() || 'individual');
    payload.tax_ids = Boolean(form?.withoutTaxes)
      ? []
      : (Array.isArray(form?.taxes) ? form.taxes : [])
        .map((item) => toPositiveOptionId(item))
        .filter((id) => id > 0);
    payload.tax_group_id = Boolean(form?.withoutTaxes) ? 0 : toPositiveOptionId(form?.taxGroup);
    payload.tax_preview = flatRateTaxPreview;
  }

  return payload;
};
